/**
 * useFormDraft Hook
 * 
 * Auto-saves form data to localStorage and recovers on page reload.
 * Provides draft management (save, load, clear) for forms.
 * 
 * Usage:
 * const { loadDraft, saveDraft, clearDraft, hasDraft } = useFormDraft('product-form');
 * 
 * // On mount, check for existing draft
 * useEffect(() => {
 *   const draft = loadDraft();
 *   if (draft) reset(draft);
 * }, []);
 * 
 * // Auto-save on changes
 * useEffect(() => {
 *   if (isDirty) saveDraft(formValues);
 * }, [formValues, isDirty]);
 * 
 * // Clear on successful save
 * onSave(() => { clearDraft(); });
 */
import { useState, useEffect, useCallback, useRef } from 'react';

const DRAFT_PREFIX = 'form_draft_';
const DRAFT_EXPIRY_MS = 24 * 60 * 60 * 1000; // 24 hours

/**
 * Get storage key for a form
 */
function getStorageKey(formId, entityId) {
  return entityId 
    ? `${DRAFT_PREFIX}${formId}_edit_${entityId}`
    : `${DRAFT_PREFIX}${formId}_new`;
}

/**
 * useFormDraft Hook
 */
export function useFormDraft(formId, options = {}) {
  const {
    entityId = null,        // ID of entity being edited (null for new)
    debounceMs = 1000,      // Debounce delay for auto-save
    enabled = true,         // Enable/disable draft functionality
    onDraftLoaded = null,   // Callback when draft is loaded
  } = options;

  const [hasDraft, setHasDraft] = useState(false);
  const [draftTimestamp, setDraftTimestamp] = useState(null);
  const saveTimeoutRef = useRef(null);
  const storageKey = getStorageKey(formId, entityId);

  // Check for existing draft on mount
  useEffect(() => {
    if (!enabled) return;
    
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const { data, timestamp } = JSON.parse(stored);
        const age = Date.now() - timestamp;
        
        if (age < DRAFT_EXPIRY_MS) {
          setHasDraft(true);
          setDraftTimestamp(new Date(timestamp));
        } else {
          // Draft expired, remove it
          localStorage.removeItem(storageKey);
        }
      }
    } catch (e) {
      console.warn('Error checking draft:', e);
    }
  }, [storageKey, enabled]);

  // Load draft from localStorage
  const loadDraft = useCallback(() => {
    if (!enabled) return null;
    
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const { data, timestamp } = JSON.parse(stored);
        const age = Date.now() - timestamp;
        
        if (age < DRAFT_EXPIRY_MS) {
          onDraftLoaded?.(data);
          return data;
        }
      }
    } catch (e) {
      console.warn('Error loading draft:', e);
    }
    return null;
  }, [storageKey, enabled, onDraftLoaded]);

  // Save draft to localStorage (debounced)
  const saveDraft = useCallback((data) => {
    if (!enabled) return;
    
    // Clear any pending save
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    // Debounce the save
    saveTimeoutRef.current = setTimeout(() => {
      try {
        const stored = {
          data,
          timestamp: Date.now(),
          formId,
          entityId,
        };
        localStorage.setItem(storageKey, JSON.stringify(stored));
        setHasDraft(true);
        setDraftTimestamp(new Date());
      } catch (e) {
        console.warn('Error saving draft:', e);
      }
    }, debounceMs);
  }, [storageKey, enabled, debounceMs, formId, entityId]);

  // Save draft immediately (no debounce)
  const saveDraftNow = useCallback((data) => {
    if (!enabled) return;
    
    // Clear any pending save
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    try {
      const stored = {
        data,
        timestamp: Date.now(),
        formId,
        entityId,
      };
      localStorage.setItem(storageKey, JSON.stringify(stored));
      setHasDraft(true);
      setDraftTimestamp(new Date());
    } catch (e) {
      console.warn('Error saving draft:', e);
    }
  }, [storageKey, enabled, formId, entityId]);

  // Clear draft from localStorage
  const clearDraft = useCallback(() => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    try {
      localStorage.removeItem(storageKey);
      setHasDraft(false);
      setDraftTimestamp(null);
    } catch (e) {
      console.warn('Error clearing draft:', e);
    }
  }, [storageKey]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  // Format draft timestamp for display
  const formatDraftTime = useCallback(() => {
    if (!draftTimestamp) return '';
    
    const now = new Date();
    const diff = now - draftTimestamp;
    
    if (diff < 60000) return 'agora mesmo';
    if (diff < 3600000) return `há ${Math.floor(diff / 60000)} min`;
    if (diff < 86400000) return `há ${Math.floor(diff / 3600000)}h`;
    return draftTimestamp.toLocaleDateString('pt-BR');
  }, [draftTimestamp]);

  return {
    hasDraft,
    draftTimestamp,
    loadDraft,
    saveDraft,
    saveDraftNow,
    clearDraft,
    formatDraftTime,
    storageKey,
  };
}

/**
 * Clear all drafts from localStorage
 */
export function clearAllDrafts() {
  const keys = Object.keys(localStorage).filter(k => k.startsWith(DRAFT_PREFIX));
  keys.forEach(k => localStorage.removeItem(k));
  return keys.length;
}

/**
 * Get all active drafts
 */
export function getAllDrafts() {
  const drafts = [];
  const keys = Object.keys(localStorage).filter(k => k.startsWith(DRAFT_PREFIX));
  
  keys.forEach(key => {
    try {
      const stored = JSON.parse(localStorage.getItem(key));
      const age = Date.now() - stored.timestamp;
      
      if (age < DRAFT_EXPIRY_MS) {
        drafts.push({
          key,
          ...stored,
          age,
        });
      }
    } catch (e) {
      // Invalid draft, skip
    }
  });
  
  return drafts;
}

export default useFormDraft;
